import { ref } from "vue";

import { SiteType } from "@appEnum/SiteType";
import { SiteMenuType } from "@du/masterTemplates/components/menus/enums";
import { markSelectedNavItems } from "@du/masterTemplates/components/menus/navSelection";
import type ICachedMenus from "@du/masterTemplates/interfaces/ICachedMenus";
import type IMenuResponse from "@du/masterTemplates/interfaces/IMenuResponse";
import { deleteObject, getObject, isStorageAvailable, setObject } from "@utility/storageHelpers";
import { defineStore } from "pinia";
import { fetchData } from "../utility/webServices";
import { useAppStore } from "./appStore";

const MENUS_STORAGE_KEY = "cpMenus";

export const useMenuStore = defineStore("menuStore", () => {
  const myMenus = ref<IMenuResponse[] | null>(null);
  const isLoadingMenus = ref<boolean>(false);
  //shared by every caller that asks while the request is out, so the page makes one menu request
  let menusInFlight: Promise<IMenuResponse[] | null> | null = null;

  const getMenus = (): Promise<IMenuResponse[] | null> => {
    if (myMenus.value !== null) {
      return Promise.resolve(myMenus.value);
    }
    if (menusInFlight !== null) {
      return menusInFlight;
    }
    const cachedMenus = readCachedMenus();
    if (cachedMenus !== null) {
      myMenus.value = cachedMenus;
      return Promise.resolve(cachedMenus);
    }

    isLoadingMenus.value = true;
    menusInFlight = fetchData<IMenuResponse[]>("UserManagement/get-my-menus", {
      SiteType: useAppStore().appArgs?.SiteTypeId ?? 1,
      MenuTypes: getMenuTypes(),
      CurrentUrl: window.location.href,
    })
      .then((mnus) => {
        if (mnus) {
          myMenus.value = mnus;
          writeCachedMenus(mnus);
        }
        return mnus ?? null;
      })
      .finally(() => {
        //cleared on failure too, so a later call can retry
        menusInFlight = null;
        isLoadingMenus.value = false;
      });
    return menusInFlight;
  };

  const getMenuCacheOwner = (): { UserId: number; SiteBrandTypeId: number } | null => {
    const appArgs = useAppStore().appArgs;
    const userId = appArgs?.CurrentUserObj?.UserId ?? 0;
    if (
      appArgs?.SiteTypeId !== SiteType.CustomerWeb ||
      userId <= 0 ||
      !isStorageAvailable("sessionStorage")
    ) {
      return null;
    }
    return { UserId: userId, SiteBrandTypeId: appArgs.SiteBrandTypeId ?? 0 };
  };

  const readCachedMenus = (): IMenuResponse[] | null => {
    const owner = getMenuCacheOwner();
    if (owner === null) {
      return null;
    }
    try {
      const cached = getObject<ICachedMenus>(MENUS_STORAGE_KEY, "sessionStorage");
      return cached?.UserId === owner.UserId && cached.SiteBrandTypeId === owner.SiteBrandTypeId
        ? cached.Menus
        : null;
    } catch {
      return null;
    }
  };

  const writeCachedMenus = (menus: IMenuResponse[]) => {
    const owner = getMenuCacheOwner();
    if (owner !== null) {
      setObject<ICachedMenus>({ ...owner, Menus: menus }, MENUS_STORAGE_KEY, "sessionStorage");
    }
  };

  const clearCachedMenus = () => {
    myMenus.value = null;
    if (isStorageAvailable("sessionStorage")) {
      deleteObject(MENUS_STORAGE_KEY, "sessionStorage");
    }
  };

  const markSelectedPath = (path: string) => {
    myMenus.value?.forEach((m) => markSelectedNavItems(m.MenuItems, path));
  };

  const getMenuTypes = (): SiteMenuType[] => {
    const siteTypeId = useAppStore().appArgs?.SiteTypeId ?? SiteType.S1;
    switch (siteTypeId) {
      case SiteType.S1:
        return [SiteMenuType.FavoritesMenu, SiteMenuType.S1InternalMainNav];
      case SiteType.CustomerWeb:
        return [SiteMenuType.CustomerWebMainNav, SiteMenuType.CustomerWebMainSecondaryNav];
      default:
        console.error(`There is no menu type mapping for a siteType of ${siteTypeId}`);
        return [];
    }
  };

  const isMenuItemFavorite = async (menuItemId: number): Promise<boolean> => {
    await getMenus();
    const menuItem = myMenus.value
      ?.find((m) => m.MenuType == -1)
      ?.MenuItems.find((m) => m.MenuItemId === menuItemId);
    return menuItem?.MenuItemUserFavoriteId ? true : false;
  };

  return { menus: myMenus, isLoadingMenus, getMenus, clearCachedMenus, markSelectedPath, isMenuItemFavorite };
});
